import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import http from '../api/http';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setToken(null);
    delete http.defaults.headers.common['Authorization'];
  }, []);

  const refreshMe = useCallback(async () => {
    const t = localStorage.getItem('token');
    if (!t) return null;
    try {
      const { data } = await http.get('/me');
      if (data?.user && Array.isArray(data.user.permissions)) {
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        return data.user;
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        logout();
      }
    }
    return null;
  }, [logout]);

  useEffect(() => {
    const initializeAuth = async () => {
      if (token) {
        try {
          const storedUser = JSON.parse(localStorage.getItem('user'));
          if (storedUser && storedUser.permissions) {
            setUser(storedUser);
            http.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            // 用后端实时权限刷新本地缓存（后端 authenticateToken 会从DB取权限）
            try {
              await refreshMe();
            } catch (refreshErr) {
              // token 失效则直接登出，避免进入半登录状态
              const status = refreshErr?.response?.status;
              if (status === 401 || status === 403) {
                logout();
              }
            }
          } else {
            console.warn("Auth: Token found but user data is invalid or missing permissions. Logging out.");
            logout();
          }
        } catch (error) {
          console.error("Auth: Failed to parse user data from localStorage. Logging out.", error);
          logout();
        }
      }
      setLoading(false);
    };
    initializeAuth();
  }, [token, logout, refreshMe]);

  const login = (userData, userToken) => {
    if (!userData || !userToken || !userData.permissions) {
      console.error("Auth: Login failed. Invalid user data or token provided.", { userData, userToken });
      return;
    }
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', userToken);
    setUser(userData);
    setToken(userToken);
    http.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
  };

  const hasPermission = useCallback((requiredPermissions) => {
    if (!user || !Array.isArray(user.permissions)) {
      return false;
    }

    const permissionsToCheck = Array.isArray(requiredPermissions) 
      ? requiredPermissions 
      : [requiredPermissions];

    if (permissionsToCheck.length === 0) {
      return true;
    }
    
    const userPermissions = new Set(user.permissions);
    const result = permissionsToCheck.every(p => userPermissions.has(p));
      
    return result;
  }, [user]);

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!user,
    hasPermission,
    refreshMe,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
